/**
 * Spotlight tool metadata registration
 * Feature: 049-critique-photo-markup
 *
 * Spotlight renders a rectangle with dimming overlay outside the spotlight area
 */

import React from 'react';
import type { MarkupShape, SpotlightShape } from '@/types/markup.types';
import { isSpotlightShape } from '@/types/markup.types';
import { registerTool } from '@/utils/markupToolRegistry';
import type { HandlePosition, ToolMetadata, ShapeRenderProps } from '@/utils/markupToolRegistry';
import { SpotlightTool } from './SpotlightTool';
import { SpotlightIcon } from '../ToolIcons';

/**
 * Spotlight tool metadata
 */
const spotlightToolMetadata: ToolMetadata = {
  type: 'spotlight',
  label: 'Spotlight',
  icon: SpotlightIcon,
  Component: SpotlightTool,
  zOrder: -1, // Render behind other shapes (dimming effect should be in background)

  /**
   * Get handle positions for spotlight
   * Handle 1: Top-left corner
   * Handle 2: Bottom-right corner
   */
  getHandles: (shape: MarkupShape): HandlePosition[] => {
    if (!isSpotlightShape(shape)) return [];

    return [
      {
        id: 'handle1',
        x: shape.geometry.x,
        y: shape.geometry.y,
        cursor: 'nwse-resize',
        label: 'Resize from top-left corner',
      },
      {
        id: 'handle2',
        x: shape.geometry.x + shape.geometry.width,
        y: shape.geometry.y + shape.geometry.height,
        cursor: 'nwse-resize',
        label: 'Resize from bottom-right corner',
      },
    ];
  },

  /**
   * Update spotlight from handle drag
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  updateFromHandle: (
    shape: MarkupShape,
    handleId: 'handle1' | 'handle2',
    x: number,
    y: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isSpotlightShape(shape)) return shape;

    if (handleId === 'handle1') {
      // Dragging top-left corner (image-relative: 0-100)
      const newX = Math.max(0, Math.min(x, shape.geometry.x + shape.geometry.width - 1));
      const newY = Math.max(0, Math.min(y, shape.geometry.y + shape.geometry.height - 1));
      return {
        ...shape,
        geometry: {
          x: newX,
          y: newY,
          width: shape.geometry.x + shape.geometry.width - newX,
          height: shape.geometry.y + shape.geometry.height - newY,
        },
      } as SpotlightShape;
    } else {
      // Dragging bottom-right corner (image-relative: 0-100)
      const newWidth = Math.max(1, x - shape.geometry.x);
      const newHeight = Math.max(1, y - shape.geometry.y);
      return {
        ...shape,
        geometry: {
          x: shape.geometry.x,
          y: shape.geometry.y,
          width: Math.min(100 - shape.geometry.x, newWidth),
          height: Math.min(100 - shape.geometry.y, newHeight),
        },
      } as SpotlightShape;
    }
  },

  /**
   * Translate spotlight by deltaX and deltaY
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  translateShape: (
    shape: MarkupShape,
    deltaX: number,
    deltaY: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isSpotlightShape(shape)) return shape;

    return {
      ...shape,
      geometry: {
        x: Math.max(0, Math.min(100 - shape.geometry.width, shape.geometry.x + deltaX)),
        y: Math.max(0, Math.min(100 - shape.geometry.height, shape.geometry.y + deltaY)),
        width: shape.geometry.width,
        height: shape.geometry.height,
      },
    } as SpotlightShape;
  },

  /**
   * Render spotlight shape as SVG with dimming overlay
   * Uses SVG mask to create the spotlight effect
   */
  renderShape: ({ shape, isSelected, onMouseDown, viewBoxHeight }: ShapeRenderProps): React.ReactElement => {
    if (!isSpotlightShape(shape)) return React.createElement('g');

    // Calculate responsive fontSize based on smaller dimension
    const smallerDimension = Math.min(100, viewBoxHeight);
    const fontSize = smallerDimension * 0.04;
    const labelRadius = smallerDimension * 0.03;
    const labelOffset = labelRadius + 1; // Offset to move label away from shape edge

    // Calculate image center
    const imageCenterX = 50;
    const imageCenterY = viewBoxHeight / 2;

    // Calculate spotlight center
    const spotlightCenterX = shape.geometry.x + shape.geometry.width / 2;
    const spotlightCenterY = shape.geometry.y + shape.geometry.height / 2;

    // Find which edge is closest to image center
    const dx = imageCenterX - spotlightCenterX;
    const dy = imageCenterY - spotlightCenterY;

    let labelX: number;
    let labelY: number;

    // Determine closest edge based on direction to image center
    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal edges are closer
      if (dx > 0) {
        // Right edge is closer to center
        labelX = shape.geometry.x + shape.geometry.width + labelOffset;
        labelY = spotlightCenterY;
      } else {
        // Left edge is closer to center
        labelX = shape.geometry.x - labelOffset;
        labelY = spotlightCenterY;
      }
    } else {
      // Vertical edges are closer
      if (dy > 0) {
        // Bottom edge is closer to center
        labelX = spotlightCenterX;
        labelY = shape.geometry.y + shape.geometry.height + labelOffset;
      } else {
        // Top edge is closer to center
        labelX = spotlightCenterX;
        labelY = shape.geometry.y - labelOffset;
      }
    }
    const className = `markup-shape ${isSelected ? 'selected' : ''}`;
    const style: React.CSSProperties = {
      cursor: isSelected ? 'grab' : 'pointer',
      pointerEvents: 'auto',
    };

    // Unique mask ID for this spotlight shape
    const maskId = `spotlight-mask-${shape.id}`;

    return React.createElement(
      'g',
      { key: shape.id, className: 'spotlight-group' },

      // Define mask for spotlight effect
      React.createElement(
        'defs',
        {},
        React.createElement(
          'mask',
          { id: maskId },
          // White background (visible area - everything outside spotlight)
          React.createElement('rect', {
            x: 0,
            y: 0,
            width: 100,
            height: viewBoxHeight,
            fill: 'white',
          }),
          // Black spotlight area (invisible - cuts hole in dimming)
          React.createElement('rect', {
            x: shape.geometry.x,
            y: shape.geometry.y,
            width: shape.geometry.width,
            height: shape.geometry.height,
            fill: 'black',
          })
        )
      ),

      // Dimming overlay (50% black) with mask applied
      React.createElement('rect', {
        x: 0,
        y: 0,
        width: 100,
        height: viewBoxHeight,
        fill: 'black',
        fillOpacity: '0.5',
        mask: `url(#${maskId})`,
        style: { pointerEvents: 'none' }, // Dimming doesn't capture clicks
      }),

      // Spotlight rectangle outline
      React.createElement('rect', {
        className,
        x: shape.geometry.x,
        y: shape.geometry.y,
        width: shape.geometry.width,
        height: shape.geometry.height,
        stroke: shape.strokeColor,
        strokeWidth: isSelected ? shape.strokeWidth * 1.5 : shape.strokeWidth,
        strokeDasharray: shape.strokeDasharray,
        fill: 'none',
        vectorEffect: 'non-scaling-stroke',
        style,
        onMouseDown,
      }),

      // Shape number label background
      React.createElement('circle', {
        cx: labelX,
        cy: labelY,
        r: labelRadius,
        fill: 'black',
        fillOpacity: '0.5',
        stroke: 'white',
        strokeWidth: '0.3',
        vectorEffect: 'non-scaling-stroke',
        style,
        onMouseDown,
      }),

      // Shape number label text
      React.createElement(
        'text',
        {
          x: labelX,
          y: labelY,
          fontSize: String(fontSize),
          fontWeight: 'bold',
          fill: 'white',
          textAnchor: 'middle',
          dominantBaseline: 'central',
          style: { pointerEvents: 'none', userSelect: 'none' },
        },
        shape.number
      )
    );
  },
};

// Register the tool
registerTool(spotlightToolMetadata);

export default spotlightToolMetadata;
